import { apiFetch } from './client'

interface NonceResponse {
  walletAddress: string
  nonce: string
  message: string
}

export interface AuthTokenResponse {
  accessToken: string
  refreshToken: string | null
  tokenType: string
  expiresIn: number
}

export function getNonce(walletAddress: string): Promise<NonceResponse> {
  return apiFetch<NonceResponse>(
    `/api/auth/nonce?walletAddress=${encodeURIComponent(walletAddress)}`,
  )
}

export function verifySignature(
  walletAddress: string,
  nonce: string,
  signature: string,
): Promise<AuthTokenResponse> {
  return apiFetch<AuthTokenResponse>('/api/auth/verify', {
    method: 'POST',
    body: JSON.stringify({ walletAddress, nonce, signature }),
  })
}

// Refresh tokens are single-use on the backend (atomic rotate-or-reject —
// see RefreshTokenService) — presenting one a second time gets a 401 "reuse
// detected", even if the first presentation is what actually rotated it.
// usePortfolioStream() runs as up to 3 concurrent instances (Header,
// Dashboard, the earn tab) that all read the same sessionStorage token and
// will all notice it's expired at once, so without this dedup they'd file 3
// simultaneous refresh calls for the identical token: one wins, the other
// two get a false "reuse detected" 401 for a session that's completely
// healthy. Callers presenting the same refreshToken while a request for it
// is already in flight share that one request instead of firing their own.
let inFlightRefresh: { token: string; promise: Promise<AuthTokenResponse> } | null = null

export function refreshAccessToken(refreshToken: string): Promise<AuthTokenResponse> {
  if (inFlightRefresh && inFlightRefresh.token === refreshToken) {
    return inFlightRefresh.promise
  }
  const promise = apiFetch<AuthTokenResponse>('/api/auth/refresh', {
    method: 'POST',
    body: JSON.stringify({ refreshToken }),
  }).finally(() => {
    if (inFlightRefresh?.token === refreshToken) inFlightRefresh = null
  })
  inFlightRefresh = { token: refreshToken, promise }
  return promise
}
